/**
 * DecisionsRoom — Mission Control Phase B.
 *
 * Full decision queue (BRD §8):
 *   - Velocity warning banner (5 decisions in 90 s)
 *   - Filter bar (source / stakes / sort)
 *   - Decision card queue with keyboard j/k navigation
 *   - Empty state + loading skeleton
 *   - Resolved today (collapsed by default)
 *   - Undo banner with animated countdown
 */
import { useState, useEffect, useRef, useCallback } from "react";
import { toast } from "sonner";
import {
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  AlertTriangle,
} from "lucide-react";

import DecisionCard    from "../decision/DecisionCard";
import DecisionFilters from "../decision/DecisionFilters";
import UndoBanner      from "../decision/UndoBanner";
import { useDecisions } from "../hooks/useDecisions";

// ── Constants ─────────────────────────────────────────────────────────────────

const VELOCITY_WINDOW_MS  = 90_000;  // 90 seconds
const VELOCITY_THRESHOLD  = 5;        // decisions that trigger warning

// ── Helpers ───────────────────────────────────────────────────────────────────

function todayMidnight() {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
}

function snoozeLabel(minutes) {
  if (minutes < 60)    return `${minutes} min`;
  if (minutes < 1440)  return `${minutes / 60}h`;
  if (minutes < 10080) return `${minutes / 1440}d`;
  return "1 week";
}

// ── DecisionsSkeleton ─────────────────────────────────────────────────────────

function DecisionsSkeleton() {
  return (
    <div className="mc-decisions-skeleton" aria-hidden="true" aria-label="Loading decisions">
      {[0, 1, 2].map((i) => (
        <div key={i} className="mc-skeleton" style={{ animationDelay: `${i * 120}ms` }}>
          <div className="mc-skeleton-line mc-skeleton-line--short" />
          <div className="mc-skeleton-line" />
          <div className="mc-skeleton-line mc-skeleton-line--medium" />
          <div className="mc-skeleton-actions">
            <div className="mc-skeleton-btn" />
            <div className="mc-skeleton-btn" />
            <div className="mc-skeleton-btn mc-skeleton-btn--wide" />
          </div>
        </div>
      ))}
    </div>
  );
}

// ── VelocityBanner ────────────────────────────────────────────────────────────

function VelocityBanner({ onDismiss, onReview }) {
  return (
    <div className="mc-velocity-banner" role="alert">
      <AlertTriangle size={14} aria-hidden="true" className="mc-velocity-icon" />
      <span className="mc-velocity-text">
        Slow down — you have made {VELOCITY_THRESHOLD} decisions in 90 seconds.
        Review your last 3.
      </span>
      <button
        className="mc-velocity-review-btn"
        onClick={onReview}
        aria-label="Review last 3 decided items"
      >
        Review
      </button>
      <button
        className="mc-velocity-dismiss"
        onClick={onDismiss}
        aria-label="Dismiss velocity warning"
      >
        ✕
      </button>
    </div>
  );
}

// ── ResolvedToday ─────────────────────────────────────────────────────────────

function ResolvedToday({ decisions }) {
  const [open, setOpen] = useState(false);

  const midnight = todayMidnight();
  const resolved = decisions.filter((d) => {
    const at = d.decided_at || d.resolved_at;
    return at && new Date(at) >= midnight;
  });

  if (resolved.length === 0) return null;

  return (
    <div className="mc-resolved-today">
      <button
        className="mc-resolved-toggle"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls="mc-resolved-list"
      >
        <span className="mc-resolved-label">
          Resolved today: {resolved.length} decision{resolved.length !== 1 ? "s" : ""}
        </span>
        {open ? (
          <ChevronUp size={14} aria-hidden="true" />
        ) : (
          <ChevronDown size={14} aria-hidden="true" />
        )}
      </button>

      {open && (
        <ul id="mc-resolved-list" className="mc-resolved-list">
          {resolved.map((d) => {
            const title =
              d._type === "legal"
                ? d.subject || `Legal · ${d.category}`
                : d.context?.title ||
                  d.context?.subject ||
                  `${d.agent_id} — ${d.recommendation || "decision"}`;

            const choiceMade =
              d._type === "legal" ? (d.status || "resolved") : (d.decision || d.founder_choice || "—");

            const decidedAt = d.decided_at || d.resolved_at;
            const time = decidedAt
              ? new Date(decidedAt).toLocaleTimeString("en-US", {
                  timeZone: "Asia/Dubai",
                  hour: "2-digit",
                  minute: "2-digit",
                  hour12: false,
                })
              : "—";

            return (
              <li key={d.id} className="mc-resolved-item">
                <span className="mc-resolved-title">{title}</span>
                <span className="mc-resolved-choice mc-mono">{choiceMade}</span>
                <span className="mc-resolved-time mc-mono">{time}</span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

// ── DecisionsRoom ─────────────────────────────────────────────────────────────

export default function DecisionsRoom() {
  // ── Filters ─────────────────────────────────────────────────────────────────

  const [filters, setFilters] = useState({
    source: "all",
    stakes: null,
    urgency: null,
    agentId: null,
    sort: "priority",
    includeDecided: false,
  });

  // ── Data ─────────────────────────────────────────────────────────────────────

  const {
    decisions,
    totalPlatform,
    totalLegal,
    loading,
    decideAction,
    snoozeAction,
    legalDecideAction,
    refetch: refetchDecisions,
  } = useDecisions(filters);

  // For ResolvedToday we also need decided items — fetched via a separate
  // useDecisions instance with includeDecided: true (no polling needed, just
  // the initial fetch, so we use a local state approach instead).
  const [resolvedAll, setResolvedAll] = useState([]);
  const { decisions: decidedDecisions } = useDecisions({ includeDecided: true });
  useEffect(() => {
    setResolvedAll(decidedDecisions);
  }, [decidedDecisions]);

  // ── Derived counts ────────────────────────────────────────────────────────────

  const pendingCount  = decisions.length;
  const wakeWorthy    = decisions.filter(
    (d) =>
      String(d.stakes  || "").toLowerCase() === "high" &&
      String(d.urgency || "").toLowerCase() === "high"
  ).length;

  // ── Keyboard navigation ───────────────────────────────────────────────────────

  const [focusedIdx, setFocusedIdx] = useState(0);

  useEffect(() => {
    function onKeyDown(e) {
      // Skip if focus is in an input / textarea / select
      const tag = document.activeElement?.tagName?.toLowerCase();
      if (tag === "input" || tag === "textarea" || tag === "select") return;

      if (e.key === "j" || e.key === "ArrowDown") {
        e.preventDefault();
        setFocusedIdx((i) => Math.min(i + 1, decisions.length - 1));
      } else if (e.key === "k" || e.key === "ArrowUp") {
        e.preventDefault();
        setFocusedIdx((i) => Math.max(i - 1, 0));
      } else if (e.key === "a") {
        // Approve / first action on focused card
        const d = decisions[focusedIdx];
        if (d) {
          const choice = d._type === "legal" ? null : (d.recommendation || "approve");
          if (d._type === "legal") {
            handleAction(d, "legal_decide", { action: "approve" });
          } else {
            handleAction(d, "decide", { decision: choice });
          }
        }
      } else if (e.key === "r") {
        const d = decisions[focusedIdx];
        if (d) {
          if (d._type === "legal") {
            handleAction(d, "legal_decide", { action: "reject" });
          } else {
            handleAction(d, "decide", { decision: "reject" });
          }
        }
      } else if (e.key === "s") {
        const d = decisions[focusedIdx];
        if (d && d._type !== "legal") {
          handleAction(d, "snooze", { minutes: 60 });
        }
      }
    }

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [decisions, focusedIdx]);

  // Clamp focused index if list shrinks
  useEffect(() => {
    if (focusedIdx >= decisions.length && decisions.length > 0) {
      setFocusedIdx(decisions.length - 1);
    }
  }, [decisions.length, focusedIdx]);

  // ── Velocity tracking ─────────────────────────────────────────────────────────

  const decisionTimestamps = useRef([]);
  const [velocityWarning, setVelocityWarning]             = useState(false);
  const [highlightLastThree, setHighlightLastThree]       = useState(false);
  const [lastThreeIds, setLastThreeIds]                   = useState([]);

  function recordDecisionTimestamp() {
    const now = Date.now();
    const timestamps = [...decisionTimestamps.current, now];
    // Keep only timestamps within the velocity window
    const recent = timestamps.filter((t) => now - t <= VELOCITY_WINDOW_MS);
    decisionTimestamps.current = recent;

    if (recent.length >= VELOCITY_THRESHOLD && !velocityWarning) {
      setVelocityWarning(true);
    }
  }

  // ── Undo state ────────────────────────────────────────────────────────────────

  const [pendingUndo, setPendingUndo] = useState(null);

  // ── Action handler ────────────────────────────────────────────────────────────

  const handleAction = useCallback(
    async (decision, actionType, extra) => {
      try {
        if (actionType === "decide") {
          await decideAction(decision.id, extra);
          recordDecisionTimestamp();
          const title =
            decision.context?.title ||
            decision.context?.subject ||
            `${decision.agent_id} — ${decision.recommendation || "decision"}`;
          setPendingUndo({
            decisionId: decision.id,
            type: "platform",
            label: title,
            timestamp: Date.now(),
          });
          // Track the ID for velocity review highlight
          setLastThreeIds((prev) => [decision.id, ...prev].slice(0, 3));

        } else if (actionType === "snooze") {
          await snoozeAction(decision.id, extra.minutes);
          toast(`Snoozed ${snoozeLabel(extra.minutes)}`);

        } else if (actionType === "legal_decide") {
          await legalDecideAction(decision.id, extra);
          recordDecisionTimestamp();
          const title = decision.subject || `Legal · ${decision.category}`;
          setPendingUndo({
            decisionId: decision.id,
            type: "legal",
            label: title,
            timestamp: Date.now(),
          });
          setLastThreeIds((prev) => [decision.id, ...prev].slice(0, 3));
        }
      } catch (err) {
        const msg = err?.response?.data?.detail || err.message || "Action failed";
        toast.error(msg);
      }
    },
    [decideAction, snoozeAction, legalDecideAction, velocityWarning] // eslint-disable-line react-hooks/exhaustive-deps
  );

  // ── Undo handler ──────────────────────────────────────────────────────────────
  // UndoBanner handles the HTTP call itself and calls onUndo(decisionId) on
  // success, so we just need to refetch to make the item reappear.

  const handleUndo = useCallback((_decisionId) => {
    setPendingUndo(null);
    refetchDecisions();
  }, [refetchDecisions]);

  // ── Render ────────────────────────────────────────────────────────────────────

  return (
    <section className="mc-room mc-room--decisions" aria-label="Decisions room">

      {/* Room header */}
      <div className="mc-room-header">
        <div>
          <div className="mc-room-eyebrow">ROOM · DECISIONS</div>
          <h2 className="mc-room-title">Decisions</h2>
        </div>
        <div className="mc-room-meta">
          {pendingCount} pending
          {wakeWorthy > 0 && (
            <span className="mc-decisions-meta-wake">
              {" "}· {wakeWorthy} wake-worthy
            </span>
          )}
        </div>
      </div>

      {/* Velocity warning */}
      {velocityWarning && (
        <VelocityBanner
          onDismiss={() => {
            setVelocityWarning(false);
            setHighlightLastThree(false);
          }}
          onReview={() => setHighlightLastThree(true)}
        />
      )}

      {/* Filters bar */}
      <DecisionFilters
        filters={filters}
        onFiltersChange={setFilters}
        pendingCount={pendingCount}
        legalCount={totalLegal}
      />

      {/* Decision queue */}
      <div className="mc-decision-queue">

        {/* Loading skeleton — first paint only */}
        {loading && decisions.length === 0 && <DecisionsSkeleton />}

        {/* Decision cards */}
        {decisions.map((d, i) => (
          <DecisionCard
            key={d.id}
            decision={d}
            focused={focusedIdx === i}
            isFocused={focusedIdx === i}
            onAction={handleAction}
            highlighted={highlightLastThree && lastThreeIds.includes(d.id)}
          />
        ))}

        {/* Empty state */}
        {decisions.length === 0 && !loading && (
          <div className="mc-decisions-empty">
            <CheckCircle2 size={32} aria-hidden="true" className="mc-decisions-empty-icon" />
            <p className="mc-decisions-empty-text">
              All clear. The platform handled everything you delegated.
            </p>
          </div>
        )}
      </div>

      {/* Resolved today */}
      <ResolvedToday decisions={resolvedAll} />

      {/* Undo banner */}
      {pendingUndo && (
        <UndoBanner
          action={pendingUndo}
          onUndo={handleUndo}
          onExpire={() => setPendingUndo(null)}
        />
      )}

      {/* Keyboard hint */}
      <p className="mc-decisions-kbd-hint" aria-hidden="true">
        j/k navigate · a approve · r reject · s snooze
      </p>

      {/* ── Scoped styles ──────────────────────────────────────────────────── */}
      <style>{`
        /* Queue layout */
        .mc-decision-queue {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        /* Wake-worthy count accent */
        .mc-decisions-meta-wake {
          color: var(--color-critical);
          font-weight: 500;
        }

        /* Empty state */
        .mc-decisions-empty {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 12px;
          padding: 64px 0;
          text-align: center;
        }

        .mc-decisions-empty-icon {
          color: var(--color-green);
          opacity: 0.6;
        }

        .mc-decisions-empty-text {
          font-size: 14px;
          color: var(--color-slate-500);
          font-family: var(--font-sans);
          max-width: 320px;
          line-height: 1.6;
          margin: 0;
        }

        /* Keyboard hint */
        .mc-decisions-kbd-hint {
          font-size: 10px;
          color: var(--color-slate-600);
          font-family: var(--font-mono);
          margin: 16px 0 0;
          letter-spacing: 0.04em;
        }

        /* ── Skeleton ── */
        .mc-decisions-skeleton {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        @keyframes mc-shimmer {
          0%   { opacity: 0.4; }
          50%  { opacity: 0.8; }
          100% { opacity: 0.4; }
        }

        .mc-skeleton {
          background-color: var(--mc-surface);
          border: 1px solid var(--mc-border);
          border-radius: var(--radius-md, 6px);
          padding: 14px 16px;
          display: flex;
          flex-direction: column;
          gap: 8px;
          animation: mc-shimmer 1.6s ease-in-out infinite;
        }

        .mc-skeleton-line {
          height: 12px;
          background-color: var(--mc-border);
          border-radius: 4px;
          width: 100%;
        }

        .mc-skeleton-line--short {
          width: 30%;
        }

        .mc-skeleton-line--medium {
          width: 60%;
        }

        .mc-skeleton-actions {
          display: flex;
          gap: 6px;
          margin-top: 4px;
        }

        .mc-skeleton-btn {
          height: 28px;
          width: 72px;
          background-color: var(--mc-border);
          border-radius: var(--radius-md, 6px);
        }

        .mc-skeleton-btn--wide {
          width: 96px;
        }

        /* ── Velocity banner ── */
        .mc-velocity-banner {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 10px 14px;
          border-radius: var(--radius-md, 6px);
          background-color: rgba(224, 122, 78, 0.10);
          border: 1px solid rgba(224, 122, 78, 0.30);
          margin-bottom: 12px;
        }

        .mc-velocity-icon {
          color: #C45E28;
          flex-shrink: 0;
        }

        .mc-velocity-text {
          font-size: 13px;
          font-family: var(--font-sans);
          color: #8A3E15;
          flex: 1;
          line-height: 1.4;
        }

        .mc-shell--cockpit .mc-velocity-text {
          color: #E07A4E;
        }

        .mc-velocity-review-btn {
          font-size: 12px;
          font-family: var(--font-sans);
          font-weight: 500;
          padding: 4px 10px;
          border-radius: var(--radius-md, 6px);
          background: transparent;
          border: 1px solid rgba(224, 122, 78, 0.50);
          color: #C45E28;
          cursor: pointer;
          flex-shrink: 0;
          transition: background-color var(--dur, 150ms) var(--ease, ease);
          white-space: nowrap;
        }

        .mc-velocity-review-btn:hover {
          background-color: rgba(224, 122, 78, 0.16);
        }

        .mc-velocity-dismiss {
          background: transparent;
          border: none;
          color: var(--color-slate-500);
          font-size: 13px;
          cursor: pointer;
          flex-shrink: 0;
          padding: 2px 4px;
          line-height: 1;
          border-radius: 3px;
          transition: color var(--dur, 150ms) var(--ease, ease);
        }

        .mc-velocity-dismiss:hover {
          color: var(--mc-text);
        }

        /* ── Resolved today ── */
        .mc-resolved-today {
          margin-top: 24px;
          border-top: 1px solid var(--mc-border);
          padding-top: 16px;
        }

        .mc-resolved-toggle {
          display: flex;
          align-items: center;
          justify-content: space-between;
          width: 100%;
          background: transparent;
          border: none;
          cursor: pointer;
          padding: 0;
          color: var(--color-slate-500);
          transition: color var(--dur, 150ms) var(--ease, ease);
        }

        .mc-resolved-toggle:hover {
          color: var(--mc-text);
        }

        .mc-resolved-label {
          font-size: 12px;
          font-family: var(--font-sans);
          letter-spacing: 0.08em;
          text-transform: uppercase;
        }

        .mc-resolved-list {
          list-style: none;
          padding: 0;
          margin: 8px 0 0;
          display: flex;
          flex-direction: column;
          gap: 0;
        }

        .mc-resolved-item {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 8px 0;
          border-bottom: 1px solid var(--mc-border);
          font-size: 12px;
          font-family: var(--font-sans);
        }

        .mc-resolved-item:last-child {
          border-bottom: none;
        }

        .mc-resolved-title {
          flex: 1;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          color: var(--color-slate-400);
        }

        .mc-resolved-choice {
          font-size: 11px;
          color: var(--color-green);
          flex-shrink: 0;
        }

        .mc-resolved-time {
          font-size: 10px;
          color: var(--color-slate-500);
          flex-shrink: 0;
        }

        @media (max-width: 640px) {
          .mc-decisions-kbd-hint {
            display: none;
          }
        }
      `}</style>
    </section>
  );
}
